import { useCallback, useMemo, useState } from "react";
import { noop } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { PanelBasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

import { GET_SYSTEM_INFO, SystemsResponse } from "./GQLCalls/getSystemCall";
import { SystemItem } from "./systemSlice";
import { MAINTAIN_SYSTEM_INFO, MaintainSystemInput, MaintainSystemResponse } from "./GQLCalls/maintainSystemCall";
import { EMERGENCY_STOP_SYSTEM, EmergencyStopSystemResponse } from "./GQLCalls/emergencyStopSystemCall";

interface maintainSystemElements {
  emergencyStopSystem: (systemId: number, specifik?: Graph_QL.GQLCons) => Promise<boolean>;
}

export const useSystem = (connection: Graph_QL.GQLCons): GqlHook_Returns<SystemItem, PanelBasicQueryInfoInput> & maintainSystemElements => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const systemsState = useAppSelector(({graphQlData:{domain}}) => domain.systems);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(systemsState, Graph_QL.ToArr(connection)) as Record<string, SystemItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<SystemItem>)
  },[connection, systemsState]);

  const getSystemsInfo = async (input: PanelBasicQueryInfoInput, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    try {
      client.query<SystemsResponse>({query: GET_SYSTEM_INFO, variables: {input, ...usedConnection}})
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.systems.maintained), payload: {con: connection?.connection, data: e.data.system}});
            setCursorId(e.data.system.info.at(-1)?.systemId ?? -1)
          }
        }).catch((error) => {console.log(error);})
    } catch (error) {
      console.log(error);
    }
  }
  
  const maintainEntries = async (systemData?: Partial<DynamicDataObk<MaintainSystemInput>>) => {
    const system = systemData?.data;

    try {
      if (system) {
        client.mutate<MaintainSystemResponse>({mutation: MAINTAIN_SYSTEM_INFO, variables: {...connection.connection, system}})
          .then((response) => {
            if (response && response.data) {
              dispatch({type: String(gqlActions.domain.systems.maintained), payload: {con: connection?.connection, data: [response.data.system.maintain.updatedValues]}});
            }
          })
      }
    } catch (error) {
      console.log(error);
    }
  }

  const emergencyStopSystem = useCallback(async (systemId: number, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific ? specific.connection : connection.connection;
    try {
      return client.mutate<EmergencyStopSystemResponse>({ mutation: EMERGENCY_STOP_SYSTEM, variables: { ...usedConnection, systemId } })
        .then((e) => {
          const doneEmergencyStop = e.data?.system.emergencyStop;
          if (doneEmergencyStop) {
            return doneEmergencyStop.success ? true : false;
          }
          return false;
      }).catch((error) => {return false})
    } catch (error) {
      return false
    }
  }, [client, connection.connection])
  

  return {call: getSystemsInfo, emergencyStopSystem, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries}
} 

